import { functionalUpdate, type Updater } from '@tanstack/react-table';
import {
  Children,
  useCallback,
  useMemo,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { isSlot } from '../../../../core/utils/is-slot.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import {
  DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION,
  DATA_TABLE_DEFAULT_PAGE_SIZE_OPTIONS,
  DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
} from '../../constants.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type {
  DataTablePaginationProps,
  DataTablePaginationState,
} from './pagination-types.js';

/**
 * Returns the default page size based on `defaultPageSize` being
 * passed as a prop, or the first option of `pageSizeOptions` array
 * if `defaultPageSize` is passed.
 * @internal
 * @defaultValue 100
 */
function getDefaultPageSize(
  pageSizeOptions?: number[],
  defaultPageSize?: number,
): number {
  if (typeof defaultPageSize === 'number') {
    return defaultPageSize;
  }

  if (Array.isArray(pageSizeOptions) && pageSizeOptions[0]) {
    return pageSizeOptions[0];
  }

  return DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION;
}

/**
 * DataTable slot definition.
 * @public
 */
export function DataTablePagination(props: DataTablePaginationProps) {
  return null;
}

/**
 * Helper function that will check if a passed component is of type `DataTablePagination`
 * @internal
 */
export function _isDataTablePagination(
  child: ReactNode,
): child is ReactElement<DataTablePaginationProps> {
  return isSlot(child, DataTablePagination);
}

/**
 * Configuration hook for pagination of the DataTable.
 * @internal
 */
export function usePagination<TData extends DataTableRowData>(
  props: PropsWithChildren,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const configPagination = useDataTableConfigElement('pagination');

  const paginationElement = Children.toArray(props.children).find(
    _isDataTablePagination,
  );

  // Init the paginationOptions to make sure they are a valid boolean.
  // oxlint-disable-next-line react-hooks/immutability
  options.enablePagination = false;

  const paginationSizeOptions: readonly number[] =
    paginationElement?.props.pageSizeOptions ??
    DATA_TABLE_DEFAULT_PAGE_SIZE_OPTIONS;

  const defaultPaginationSizeOption = getDefaultPageSize(
    paginationElement?.props.pageSizeOptions,
    paginationElement?.props.defaultPageSize ??
      configPagination?.defaultPageSize,
  );

  const [pageSize, setPageSize] = useControllableState({
    value: paginationElement?.props.pageSize,
    defaultValue: defaultPaginationSizeOption,
    onChange: paginationElement?.props.onPageSizeChange,
  });

  const defaultPaginationIndexOption =
    paginationElement?.props.defaultPageIndex ??
    configPagination?.defaultPageIndex ??
    DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX;

  const [pageIndex, setPageIndex] = useControllableState({
    value: paginationElement?.props.pageIndex,
    defaultValue: defaultPaginationIndexOption,
  });

  const paginationState = useMemo<DataTablePaginationState>(() => {
    return {
      pageSize,
      pageIndex,
      enablePreviousPage: paginationElement?.props.enablePreviousPage,
      enableNextPage: paginationElement?.props.enableNextPage,
      totalRowsCount: paginationElement?.props.totalRowsCount,
    };
  }, [
    pageSize,
    pageIndex,
    paginationElement?.props.enablePreviousPage,
    paginationElement?.props.enableNextPage,
    paginationElement?.props.totalRowsCount,
  ]);

  const onPaginationChangeCallback = useCallback(
    (
      updater: Updater<DataTablePaginationState>,
      trigger: 'user' | 'internal' = 'user',
    ) => {
      const nextState: DataTablePaginationState = functionalUpdate(updater, {
        pageSize,
        pageIndex,
      });

      if (
        nextState?.pageSize !== pageSize &&
        nextState?.pageSize !== undefined
      ) {
        setPageSize(nextState.pageSize);
        setPageIndex(DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX);
        paginationElement?.props.onPageIndexChange?.(
          DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
          'internal',
        );
      } else if (
        nextState?.pageIndex !== pageIndex &&
        nextState?.pageIndex !== undefined
      ) {
        setPageIndex(nextState.pageIndex);
        paginationElement?.props.onPageIndexChange?.(
          nextState.pageIndex,
          trigger,
        );
      }
    },
    [setPageSize, setPageIndex, pageSize, pageIndex, paginationElement],
  );

  if (!paginationElement) {
    return;
  }

  // oxlint-disable-next-line react-hooks/immutability
  options.enablePagination = true;
  // oxlint-disable-next-line react-hooks/immutability
  options.availablePaginationOptions = paginationSizeOptions;
  // oxlint-disable-next-line react-hooks/immutability
  options.controlledPageIndex = paginationElement.props.pageIndex !== undefined;

  if (paginationElement.props.autoResetPageIndex === false) {
    // oxlint-disable-next-line react-hooks/immutability
    options.autoResetPageIndex = paginationElement.props.autoResetPageIndex;
  }

  // Server side pagination.
  if (
    paginationElement.props.enableNextPage !== undefined &&
    paginationElement.props.enablePreviousPage !== undefined
  ) {
    // oxlint-disable-next-line react-hooks/immutability
    options.manualPagination = true;
    // Ensure pageCount is considered unknown to react table.
    // oxlint-disable-next-line react-hooks/immutability
    options.pageCount = -1;
  } else {
    // oxlint-disable-next-line react-hooks/immutability
    options.manualPagination = false;
  }
  // oxlint-disable-next-line react-hooks/immutability
  options.state = {
    ...options.state,
    pagination: paginationState,
  };

  // oxlint-disable-next-line react-hooks/immutability
  options.onPaginationChange = onPaginationChangeCallback;
}
